import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import type { TimedSetGroupProgressPoint } from "../types";

type ProgressMetric = "volumeKg" | "volumePerMinuteKg";

interface StrengthProgressChartProps {
  data: TimedSetGroupProgressPoint[];
}

const metricConfig: Record<
  ProgressMetric,
  { key: string; color: string; unit: string }
> = {
  volumeKg: { key: "volume", color: "#9333ea", unit: "kg" },
  volumePerMinuteKg: {
    key: "volumePerMinute",
    color: "#2563eb",
    unit: "kg/min",
  },
};

export function StrengthProgressChart({ data }: StrengthProgressChartProps) {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState<ProgressMetric>("volumeKg");
  const config = metricConfig[metric];

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${value}T00:00:00`));
  const formatNumber = (value: number) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const formatPercent = (value: number | undefined) =>
    value === undefined
      ? t("strengthProgress:chart.unavailable")
      : `${formatNumber(value)}%`;

  return (
    <Card>
      <CardHeader className="gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>{t("strengthProgress:chart.comparisonTitle")}</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("strengthProgress:chart.description")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(metricConfig) as ProgressMetric[]).map(key => (
            <Button
              key={key}
              type="button"
              variant={metric === key ? "default" : "outline"}
              size="sm"
              onClick={() => setMetric(key)}
            >
              {t(`strengthProgress:chart.${metricConfig[key].key}`)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={data}
            margin={{ top: 8, right: 20, left: 4, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={formatDate} minTickGap={28} />
            <YAxis
              width={74}
              tickFormatter={value =>
                `${Number(value).toLocaleString(i18n.language)} ${config.unit}`
              }
            />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value: number) => [
                `${Number(value).toLocaleString(i18n.language, {
                  maximumFractionDigits: 1,
                })} ${config.unit}`,
                t(`strengthProgress:chart.${config.key}`),
              ]}
            />
            <Line
              type="monotone"
              dataKey={metric}
              stroke={config.color}
              strokeWidth={2.5}
              activeDot={{ r: 6 }}
              dot={{ r: data.length === 1 ? 5 : 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-5 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("strengthProgress:history.date")}</TableHead>
                <TableHead className="text-right">
                  {t("strengthProgress:fields.durationSeconds")}
                </TableHead>
                <TableHead className="text-right">
                  {t("strengthProgress:chart.volume")}
                </TableHead>
                <TableHead className="text-right">
                  {t("strengthProgress:chart.volumeChange")}
                </TableHead>
                <TableHead className="text-right">
                  {t("strengthProgress:chart.volumePerMinute")}
                </TableHead>
                <TableHead className="text-right">
                  {t("strengthProgress:chart.volumePerMinuteChange")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(point => (
                <TableRow key={point.date}>
                  <TableCell className="whitespace-nowrap font-medium">
                    {formatDate(point.date)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(point.durationSeconds)} s
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(point.volumeKg)} kg
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(point.volumeChangePercent)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(point.volumePerMinuteKg)} kg/min
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercent(point.volumePerMinuteChangePercent)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data.length === 1 && (
          <p className="mt-3 text-sm text-muted-foreground">
            {t("strengthProgress:chart.singlePoint")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
